
import { Options, mixins } from "vue-class-component";
import StoreReports from "../../service/StoreReports";
import UtilityOptions from "../../mixins/UtilityOptions";
import Toaster from "../../helpers/Toaster";
import moment from "moment";

interface StockEditReportRow {
  branchName: string;
  adjustedAt: string;
  receiptNo: string;
  reasonId: number;
  reason: string;
  batchNo: string;
  userName: string;
  fieldChanged: string;
  oldValue: string;
  newValue: string;
  qtyChange: string | number;
  paidQty: string | number;
  freeQty: string | number;
  foundQty: string | number;
  amount: number | null;
  description: string;
  sourceType: string;
}

@Options({
  title: "Stock Edit Report",
  watch: {
    lists: {
      handler(newLists: StockEditReportRow[]) {
        this.totalQtyChange  = newLists.reduce((a, r) => a + (parseFloat(r.qtyChange as any)  || 0), 0);
        this.totalPaidQty    = newLists.reduce((a, r) => a + (parseFloat(r.paidQty as any)    || 0), 0);
        this.totalFreeQty    = newLists.reduce((a, r) => a + (parseFloat(r.freeQty as any)    || 0), 0);
        this.totalFoundQty   = newLists.reduce((a, r) => a + (parseFloat(r.foundQty as any)   || 0), 0);
        this.totalAmount     = newLists.reduce((a, r) => a + (parseFloat(r.amount as any)     || 0), 0);
      },
      immediate: true,
    },
  },
})
export default class StockEditReport extends mixins(UtilityOptions) {
  private lists: StockEditReportRow[] = [];
  private reportService;
  private toast;
  private resultTitle = "";
  private productDialog = false;
  private loading = false;
  private limit = 50;
  private totalRecords = 0;
  private goToFirstLink = 0;
  private dialogTitle = "";

  private totalQtyChange = 0;
  private totalPaidQty = 0;
  private totalFreeQty = 0;
  private totalFoundQty = 0;
  private totalAmount = 0;

  private home = { icon: "pi pi-home", to: "/" };
  private items = [
    { label: "Reports", to: "/report/index" },
    { label: "Stock Edit Report", to: "/report/stock-edit-report" },
  ];

  private filterDates: any[] = [];
  private branches: any[] = [];
  private users: any[] = [];

  private reasonOptions = [
    { id: 0, name: "All" },
    { id: 1, name: "Purchase Correction" },
    { id: 2, name: "Lost" },
    { id: 3, name: "Damaged" },
    { id: 4, name: "Stock Found" },
  ];

  private searchFilters = {
    filterType: "this_month",
    date1: "",
    date2: "",
    branchId: 0,
    userId: 0,
    reasonId: 0,
  };

  onPage(event) { this.loadList(event.first); }

  created() {
    this.reportService = new StoreReports();
    this.toast = new Toaster();
  }

  mounted() {
    this.loadFilters();
    this.loadList(0);
  }

  openDialog() {
    this.dialogTitle = "Filter Stock Edit Report";
    this.productDialog = true;
  }

  loadFilters() {
    this.reportService.getFilterList().then((res) => {
      this.filterDates = res.datesList ?? [];
      this.branches    = res.branches ?? [];
      this.branches.unshift({ id: 0, name: "All Branches" });
      this.users       = res.cashiers ?? [];
      this.users.unshift({ id: 0, name: "All Users" });
    });
  }

  loadList(page) {
    this.loading = true;
    this.reportService.stockEditReport(this.searchFilters, page).then((res) => {
      const data         = this.camelizeKeys(res);
      this.resultTitle   = data.resultTitle;
      this.loading       = false;
      this.limit         = data.limit ?? 50;
      this.totalRecords  = data.totalRecords ?? 0;
      this.goToFirstLink = page;
      this.lists         = data.record ?? [];
      this.productDialog = false;
    });
  }

  get rList(): StockEditReportRow[] { return this.lists; }

  formatDateTime(val): string {
    if (!val) return "-";
    return moment(val).format("DD-MM-YYYY hh:mm A");
  }

  formatAmount(val): string {
    if (val == null || val === "" || isNaN(Number(val))) return "-";
    return `${this.currency} ${Number(val).toFixed(2)}`;
  }

  reasonBadgeClass(reasonId: number): string {
    const map: Record<number, string> = {
      1: "badge badge-info",
      2: "badge badge-danger",
      3: "badge badge-warning",
      4: "badge badge-success",
    };
    return map[reasonId] ?? "badge badge-secondary";
  }

  get currency() { return this.store.getters.getCurrency; }

  exportCSV() {
    const headers = [
      "Branch", "Date", "Receipt No", "Reason", "Batch No",
      "Adjusted By", "Field Changed", "Old Value", "New Value",
      "Qty Change", "Paid Qty", "Free/Bonus", "Found",
      "Amount", "Description",
    ];

    const rows = this.rList.map((r) => [
      r.branchName,
      this.formatDateTime(r.adjustedAt),
      r.receiptNo,
      r.reason,
      r.batchNo,
      r.userName,
      r.fieldChanged,
      r.oldValue,
      r.newValue,
      r.qtyChange,
      r.paidQty,
      r.freeQty,
      r.foundQty,
      r.amount != null ? Number(r.amount).toFixed(2) : "",
      `"${(r.description ?? "").replace(/"/g, "'")}"`,
    ]);

    const footer = [
      "Total", "", "", "", "", "", "", "", "",
      this.totalQtyChange,
      this.totalPaidQty,
      this.totalFreeQty,
      this.totalFoundQty,
      Number(this.totalAmount).toFixed(2),
      "",
    ];

    const csv = [
      headers.join(","),
      ...rows.map((r) => r.join(",")),
      footer.join(","),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "stock_edit_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
